import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { getReplayPlaybackState, seekReplayEvidence } from '../../game2';
import computePlayerRatingsForMatch from './playerRatingsShared';
import PlayerInsightContent from './PlayerInsightContent';
import { playerTeam } from '../../utils/playerInsight';

function chooseDrawerSide(playback, currentSide = 'right') {
  if (typeof window === 'undefined' || window.innerWidth <= 560) return 'right';
  const ball = playback?.ballScreen;
  if (!Number.isFinite(ball?.x) || !Number.isFinite(ball?.y)) return currentSide;
  const width = Math.min(440, Math.max(0, window.innerWidth - 28));
  const top = 14;
  const bottom = Math.max(top, window.innerHeight - 54);
  const sideBounds = (side) => side === 'left'
    ? { left: 14, right: 14 + width }
    : { left: window.innerWidth - 14 - width, right: window.innerWidth - 14 };
  const ballIsCovered = (side) => {
    const bounds = sideBounds(side);
    return ball.x >= bounds.left && ball.x <= bounds.right && ball.y >= top && ball.y <= bottom;
  };
  // Always place the drawer opposite the ball. If the ball enters the current
  // panel, flip immediately so the selected action stays visible.
  if (ballIsCovered(currentSide)) return currentSide === 'right' ? 'left' : 'right';
  const preferred = ball.x >= window.innerWidth / 2 ? 'left' : 'right';
  return ballIsCovered(preferred) ? (preferred === 'right' ? 'left' : 'right') : preferred;
}

function OverlayDrawer({ match, player, rating, onClose, side = 'right' }) {
  if (!player) return null;
  return (
    <aside className={`replay-player-drawer${side === 'left' ? ' is-left' : ''}`} aria-label={`${player} player insight`}>
      <div className="replay-player-drawer-header">
        <div>
          <div className="insight-eyebrow">FIELD PROFILE</div>
          <h2>{player}</h2>
          <p>{rating?.role?.toUpperCase() || 'PLAYER'} · {playerTeam(match, player)?.toUpperCase() || 'TEAM UNKNOWN'} · {rating ? `${Number(rating.rating).toFixed(1)}/10` : 'No rating'}</p>
        </div>
        <button type="button" className="replay-player-drawer-close" onClick={onClose} aria-label="Close player insight">×</button>
      </div>
      <div className="replay-player-drawer-scroll">
        <PlayerInsightContent match={match} player={player} rating={rating} onSeek={(tick) => seekReplayEvidence(tick)} />
      </div>
    </aside>
  );
}

export default function ReplayPlayerOverlay() {
  const mainMode = useSelector((state) => state.mainMode.value);
  const matches = useSelector((state) => state.gameStats.matches) || [];
  const selectedMatch = useSelector((state) => state.gameStats.selectedMatch) || 0;
  const match = matches[selectedMatch] || null;
  const [host, setHost] = useState(null);
  const [surfaceVisible, setSurfaceVisible] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [drawerSide, setDrawerSide] = useState('right');

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const findHost = () => {
      const next = document.querySelector('.game-state-view');
      setHost((previous) => previous === next ? previous : next);
    };
    findHost();
    const observer = new MutationObserver(findHost);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!host) return undefined;
    let frame = 0;
    const poll = () => {
      const nextPlayback = getReplayPlaybackState();
      setDrawerSide((previous) => chooseDrawerSide(nextPlayback, previous));
      let visible = false;
      try {
        const rect = host.getBoundingClientRect();
        const home = document.querySelector('.roomlist-view.home-view');
        const homeRect = home?.getBoundingClientRect?.();
        const intersectsViewport = rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.bottom > 0 && rect.left < window.innerWidth && rect.top < window.innerHeight;
        const homeCoversSurface = homeRect && homeRect.width > 0 && homeRect.height > 0 && homeRect.right > 0 && homeRect.left < window.innerWidth && getComputedStyle(home).display !== 'none' && getComputedStyle(home).visibility !== 'hidden';
        visible = Boolean(intersectsViewport && !homeCoversSurface && nextPlayback?.active);
      } catch (_) {}
      setSurfaceVisible(visible);
      frame = window.setTimeout(poll, 100);
    };
    poll();
    return () => window.clearTimeout(frame);
  }, [host]);

  useEffect(() => {
    if (mainMode !== 'home' || !surfaceVisible) setSelectedPlayer(null);
    if (mainMode !== 'home' || !surfaceVisible) setDrawerSide('right');
  }, [mainMode, surfaceVisible]);

  // The native renderer owns the canvas and knows the exact transformed
  // position/radius of every player in the frame it just drew.  It emits a
  // selection event from that hit test; this component only opens the drawer.
  // No DOM markers are painted over the field, so there can never be a second
  // set of players drifting away from the real replay discs.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleNativePlayerClick = (event) => {
      if (mainMode !== 'home' || !surfaceVisible) return;
      const name = String(event?.detail?.name || '').trim();
      if (name) setSelectedPlayer(name);
    };
    window.addEventListener('hra-replay-player-click', handleNativePlayerClick);
    return () => window.removeEventListener('hra-replay-player-click', handleNativePlayerClick);
  }, [mainMode, surfaceVisible]);

  const ratings = useMemo(() => {
    if (!match) return null;
    const data = computePlayerRatingsForMatch(match);
    return new Map([...data.red, ...data.blue].map((entry) => [entry.nick.toLowerCase(), entry]));
  }, [match]);

  if (!host || !match || mainMode !== 'home' || !surfaceVisible) return null;
  const overlay = (
    <>
      {selectedPlayer && (
        <OverlayDrawer
          match={match}
          player={selectedPlayer}
          rating={ratings?.get(selectedPlayer.toLowerCase()) || null}
          side={drawerSide}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </>
  );
  return createPortal(overlay, host);
}
